import { io } from "socket.io-client";

const getSocketUrl = () => {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host.includes("staging.artinv.online")) {
      return window.location.origin;
    }
  }
  return process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL || (typeof window !== "undefined" ? window.location.origin : "");
};

const SOCKET_URL = getSocketUrl();

/**
 * SocketHandler - Manages Socket.IO connection
 * 
 * Uses JWT authentication via auth object and query parameter fallback
 * to work with backend's @socket_jwt_required decorator
 */
export class SocketHandler {
    constructor() {
        this.socket = io(SOCKET_URL, {
            autoConnect: false,
            transports: ["websocket", "polling"],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000,
        });

        this.joinedRooms = new Set(); // Track joined room IDs
        this.connectionStateCallbacks = new Set(); // Callbacks for connection state changes
        this.isReconnecting = false;

        this._setupBaseListeners();
        this._setupGlobalLogging();
    }

    /**
     * Get JWT token from localStorage
     * @returns {string|null} JWT token
     */
    getToken() {
        return localStorage.getItem("token");
    }

    /**
     * Connect to the Socket.IO server with JWT authentication
     * @param {string} token - Optional JWT token (defaults to localStorage token)
     * @returns {boolean} Connection success
     */
    connect(token = null) {
        const authToken = token || this.getToken();

        if (!authToken) {
            console.error("[SocketHandler] No JWT token available for connection");
            return false;
        }

        if (this.socket.connected) {
            console.log("[SocketHandler] Already connected");
            return true;
        }

        // Update auth and query with the new token
        this.socket.auth = { token: authToken };
        this.socket.io.opts.query = { token: authToken };

        console.log("[SocketHandler] Connecting with token...");
        this.socket.connect();
        return true;
    }

    /**
     * Disconnect from the Socket.IO server
     */
    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
        }
        // Clear room tracking on disconnect
        this.joinedRooms.clear();
        this.isReconnecting = false;
    }

    /**
     * Get list of currently joined room IDs
     * @returns {string[]} Array of room IDs
     */
    getJoinedRooms() {
        return Array.from(this.joinedRooms);
    }

    /**
     * Check if a specific room is joined
     * @param {string} sessionId - Room ID to check
     * @returns {boolean}
     */
    isRoomJoined(sessionId) {
        return this.joinedRooms.has(sessionId);
    }

    /**
     * Setup basic connection listeners (only for debugging/status)
     * @private
     */
    _setupBaseListeners() {
        if (!this.socket) return;

        this.socket.on("connect", () => {
            console.log("[SocketHandler] Connected to server");
            this.isReconnecting = false;
            this._notifyConnectionStateChange(true);
            // Re-join all previously joined rooms after reconnection
            this._rejoinRooms();
        });

        this.socket.on("disconnect", (reason) => {
            console.log("[SocketHandler] Disconnected:", reason);
            this.isReconnecting = reason === "io server disconnect" || reason === "transport close";
            this._notifyConnectionStateChange(false);
        });

        this.socket.on("reconnect", (attemptNumber) => {
            console.log("[SocketHandler] Reconnected after", attemptNumber, "attempts");
            this.isReconnecting = false;
            this._notifyConnectionStateChange(true);
            // Re-join all previously joined rooms after reconnection
            this._rejoinRooms();
        });

        this.socket.on("reconnect_attempt", (attemptNumber) => {
            console.log("[SocketHandler] Reconnection attempt", attemptNumber);
            this.isReconnecting = true;
        });

        this.socket.on("reconnect_error", (error) => {
            console.error("[SocketHandler] Reconnection error:", error.message);
        });

        this.socket.on("reconnect_failed", () => {
            console.error("[SocketHandler] Reconnection failed");
            this.isReconnecting = false;
            this._notifyConnectionStateChange(false);
        });

        this.socket.on("connect_error", (error) => {
            console.error("[SocketHandler] Connection error:", error.message);
            
            // Check if server rejected connection due to token expiration
            if (error.message === "Token Expired") {
                this.socket.disconnect(); // Stop automatic reconnections
                this.isReconnecting = false;
                
                // Clear expired session and spit user out to relogin
                localStorage.removeItem("user");
                localStorage.removeItem("token");
                localStorage.removeItem("refresh_token");
                if (window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }
            }

            this._notifyConnectionStateChange(false);
        });
    }

    /**
     * Re-join all previously joined rooms after reconnection
     * @private
     */
    _rejoinRooms() {
        if (!this.socket?.connected || this.joinedRooms.size === 0) return;

        console.log("[SocketHandler] Re-joining", this.joinedRooms.size, "rooms after reconnection");
        // Re-join all rooms
        this.joinedRooms.forEach(sessionId => {
            if (this.socket?.connected) {
                this.socket.emit("join_bidding_room", { session_id: sessionId });
                console.log("[SocketHandler] Re-joined room:", sessionId);
            }
        });
    }

    /**
     * Notify all registered callbacks about connection state changes
     * @private
     */
    _notifyConnectionStateChange(isConnected) {
        this.connectionStateCallbacks.forEach(callback => {
            try {
                callback(isConnected);
            } catch (error) {
                console.error("[SocketHandler] Error in connection state callback:", error);
            }
        });
    }

    /**
     * Register a callback for connection state changes
     * @param {Function} callback - Callback function(isConnected: boolean) => void
     * @returns {Function} Unsubscribe function
     */
    onConnectionStateChange(callback) {
        this.connectionStateCallbacks.add(callback);
        // Return unsubscribe function
        return () => {
            this.connectionStateCallbacks.delete(callback);
        };
    }

    /**
     * Setup global logging for all incoming events
     * @private
     */
    _setupGlobalLogging() {
        if (!this.socket) return;

        this.socket.onAny((event, ...args) => {
            console.log(`%c[Socket IN] ${event}`, "color: #4CAF50; font-weight: bold", ...args);
        });
    }

    // ===== Client-to-Server Events (Emitters) =====

    /**
     * Join an auction bidding room to receive real-time updates
     * @param {string} sessionId - Auction session ID to join
     */
    joinBiddingRoom(sessionId) {
        if (!this.socket?.connected) {
            console.error("[SocketHandler] Cannot join room: not connected");
            return false;
        }

        // Track the room
        this.joinedRooms.add(sessionId);
        this.socket.emit("join_bidding_room", { session_id: sessionId });
        console.log("[SocketHandler] Joining bidding room:", sessionId);
        return true;
    }

    /**
     * Place a bid in an active auction
     * @param {string} sessionId - Auction session ID
     * @param {number} amount - Bid amount
     */
    placeBid(sessionId, amount) {
        if (!this.socket?.connected) {
            console.error("[SocketHandler] Cannot place bid: not connected");
            return false;
        }

        this.socket.emit("place_bid", {
            session_id: sessionId,
            amount: amount
        });
        console.log("[SocketHandler] Placing bid:", { sessionId, amount });
        return true;
    }

    /**
     * Leave an auction bidding room to stop receiving updates
     * @param {string} sessionId - Auction session ID to leave
     */
    leaveBiddingRoom(sessionId) {
        // Remove from tracking
        this.joinedRooms.delete(sessionId);

        if (!this.socket?.connected) {
            console.log("[SocketHandler] Not connected, but removed room from tracking:", sessionId);
            return true; // Already removed from tracking
        }

        this.socket.emit("leave_bidding_room", { session_id: sessionId });
        console.log("[SocketHandler] Leaving bidding room:", sessionId);
        return true;
    }

    /**
     * Generic emit method
     * @param {string} event 
     * @param {any} data 
     */
    emit(event, data) {
        if (!this.socket?.connected) {
            console.warn(`[SocketHandler] Cannot emit ${event}: not connected`);
            return false;
        }
        console.log(`%c[Socket OUT] ${event}`, "color: #2196F3; font-weight: bold", data);
        this.socket.emit(event, data);
        return true;
    }

    /**
     * Check if socket is currently connected
     * @returns {boolean}
     */
    get isConnected() {
        return !!this.socket?.connected;
    }

    /**
     * Get the underlying socket instance
     * @returns {Socket|null}
     */
    getSocket() {
        return this.socket;
    }
}

export default SocketHandler;
